import { createFileRoute, getRouteApi, Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Card, CardContent } from '@/components/ui/card'
import { pageLinks, pageMeta } from '@/lib/seo'

export const Route = createFileRoute('/docs/')({
  head: () => ({
    meta: pageMeta({
      title: 'Proxy Setup, Clients, and API Docs · Masklabs',
      description:
        'Guides for the Masklabs proxy network: connection strings, Python and Node.js clients, browser automation, session modes and location targeting, and billing.',
      path: '/docs',
    }),
    links: pageLinks('/docs'),
  }),
  component: DocsIndex,
})

// The grouped nav is loaded once by the /docs layout route.
const docsRoute = getRouteApi('/docs')

function DocsIndex() {
  const { groups } = docsRoute.useLoaderData()
  return (
    <div className="w-full">
      <Breadcrumb className="mb-6">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/">Home</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Docs</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <h1 className="text-3xl font-bold tracking-tight text-balance sm:text-4xl">
        Documentation
      </h1>
      <p className="mt-4 max-w-2xl text-base text-muted-foreground text-pretty">
        Connection strings, client libraries, browser automation, the API, and
        how billing works underneath it. Everything past the dashboard&apos;s
        endpoint builder.
      </p>

      <div className="mt-10 flex flex-col gap-10">
        <section>
          <h2 className="text-sm font-semibold tracking-wide text-muted-foreground uppercase">
            API
          </h2>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <Link to="/docs/api" className="group block">
              <Card className="h-full transition-colors group-hover:border-primary/40">
                <CardContent className="flex h-full flex-col gap-2 p-6">
                  <h3 className="text-lg font-semibold tracking-tight text-balance">
                    API reference
                  </h3>
                  <p className="text-sm text-muted-foreground text-pretty">
                    Authentication, conventions, errors, and every endpoint with
                    examples in curl, Python, and Node.js.
                  </p>
                  <span className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-primary">
                    Open reference
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </CardContent>
              </Card>
            </Link>
          </div>
        </section>
        {groups.map((group) => (
          <section key={group.category}>
            <h2 className="text-sm font-semibold tracking-wide text-muted-foreground uppercase">
              {group.category}
            </h2>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              {group.guides.map((guide) => (
                <Link
                  key={guide.slug}
                  to="/docs/$slug"
                  params={{ slug: guide.slug }}
                  className="group block"
                >
                  <Card className="h-full transition-colors group-hover:border-primary/40">
                    <CardContent className="flex h-full flex-col gap-2 p-6">
                      <h3 className="text-lg font-semibold tracking-tight text-balance">
                        {guide.title}
                      </h3>
                      <p className="text-sm text-muted-foreground text-pretty">
                        {guide.description}
                      </p>
                      <span className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-primary">
                        Read guide
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  )
}
